import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ShieldAlert, Loader2 } from 'lucide-react';

interface Props {
  isOpen: boolean;
  alias: string;
  action: string;
  onCancel: () => void;
  onConfirm: (typedAlias: string) => Promise<void> | void;
}

const ProductionConfirmDialog = ({ isOpen, alias, action, onCancel, onConfirm }: Props) => {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTyped('');
      setError('');
      setBusy(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (typed !== alias) {
      setError(`Type the alias exactly: ${alias}`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onConfirm(typed);
    } catch (err) {
      setError('The change could not be applied.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !busy && onCancel()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-rose-500/30 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                <AlertTriangle size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">
                  Confirm production change
                </h3>
                <p className="mt-1 text-sm text-slate-400">
                  This connection is a <span className="font-bold text-rose-400">production</span>{' '}
                  database. {action} cannot be undone.
                </p>
              </div>
            </div>

            <div className="mt-5">
              <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                Type <span className="text-cyan-400">{alias}</span> to continue
              </label>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                placeholder={alias}
                className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 outline-none transition focus:border-rose-500/50"
              />
              {error && (
                <p className="mt-2 text-xs font-bold text-rose-400">{error}</p>
              )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={onCancel}
                disabled={busy}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={busy || typed !== alias}
                className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldAlert size={15} />}
                {busy ? 'Applying' : 'Confirm'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ProductionConfirmDialog;
